import Component from '@glimmer/component'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { didCancel, task, timeout } from 'ember-concurrency'
import { tracked } from '@glimmer/tracking'
import { Changeset } from 'ember-changeset'
import lookupValidator from 'teacher-ui-client/utils/lookup-validator'
import { addTeacherValidator } from 'teacher-ui-client/teacher/validator'

import { translationForLoginExists } from 'teacher-ui-client/validations/login-exists'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Store from '@ember-data/store'
import type { IntlService } from 'ember-intl'
import type { Log } from '@blakeelearning/log'
import type ErrorHandlerService from 'teacher-ui-client/services/error-handler'
import type FileDownload from 'teacher-ui-client/services/file-download'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type FeatureControlService from 'teacher-ui-client/services/feature-control'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type School from 'teacher-ui-client/models/school'
import type ErrorHandler from 'teacher-ui-client/utils/error-handler'
import type { BufferedChangeset } from 'ember-changeset/types'
import type Teacher from 'teacher-ui-client/models/teacher'
import type { ErrorDefinition } from 'teacher-ui-client/utils/error-handler'
import { FormField } from 'teacher-ui-client/components/form-field'
import { t } from 'ember-intl'
import { HRText } from 'teacher-ui-client/components/base/hr-text'
import { UiButtonLink } from 'teacher-ui-client/components/ui-button/link'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { eq } from 'ember-truth-helpers'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { pick } from 'teacher-ui-client/helpers/pick'
import { on } from '@ember/modifier'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { UiButtonState } from 'teacher-ui-client/components/ui-button/state'
import { assert } from '@ember/debug'
import type RouterService from '@ember/routing/router-service'
import type Owner from '@ember/owner'
import type SchoolClass from 'teacher-ui-client/models/school-class'

const TEACHER_SUBSCRIPTION = 'teacher-subscription'
const SCHOOL_SUBSCRIPTION = 'school-subscription'

type TranslationForLoginExists = (error: unknown, errorDefinition: ErrorDefinition, intl: IntlService) => string

interface Args {
  subscriptionType: SubscriptionType
  school: School
  onSelectCsvFile?: (file: File) => void
  isUploading?: boolean
}

interface Signature {
  Element: HTMLFormElement
  Args: Args
}

export class AddTeacherToSchool extends Component<Signature> {
  @service store!: Store
  @service intl!: IntlService
  @service log!: Log
  @service router!: RouterService
  @service('error-handler') errorHandlerService!: ErrorHandlerService
  @service fileDownload!: FileDownload
  @service resourceConflict!: ResourceConflictService
  @service featureControl!: FeatureControlService
  @service flashQueue!: FlashQueueService

  @tracked changeset!: BufferedChangeset
  @tracked errorHandler!: ErrorHandler

  get showTrialSubscriptionOptions() {
    return this.featureControl.canAccess('create_trial_subscriptions')
  }

  get accountTypeOptions() {
    const { intl, showTrialSubscriptionOptions } = this

    if (showTrialSubscriptionOptions) {
      return [
        {
          value: SCHOOL_SUBSCRIPTION,
          uid: SCHOOL_SUBSCRIPTION,
          text: intl.t('manage.teachers.accountTypes.school'),
        },
        {
          value: TEACHER_SUBSCRIPTION,
          uid: TEACHER_SUBSCRIPTION,
          text: intl.t('manage.teachers.accountTypes.trial'),
        },
      ]
    } else {
      return [
        {
          value: SCHOOL_SUBSCRIPTION,
          uid: SCHOOL_SUBSCRIPTION,
          text: intl.t('manage.teachers.accountTypes.school'),
        },
      ]
    }
  }

  constructor(owner: Owner, args: Args) {
    super(owner, args)

    const formData = {
      firstName: '',
      lastName: '',
      email: '',
      accountType: SCHOOL_SUBSCRIPTION,
    }
    const { intl } = this
    const validator = lookupValidator(addTeacherValidator, { intl })
    const changeset = Changeset(formData, validator, addTeacherValidator)
    this.changeset = changeset

    const errorHandler = this.errorHandlerService.create()

    const currentSchoolId = this.args.school.id

    // add an error handler for when the error has code `unique`, the handler
    // will pass the error to the `errorTranslationFn` function to produce the
    // error message displayed to the user.
    errorHandler.registerErrorDefinition({
      statusCode: '422',
      errorCode: 'unique',
      changesetProperty: 'email',
      changeset,
      kind: 'changeset',
      errorTranslationFn: translationForLoginExists as TranslationForLoginExists,
      currentSchoolId: currentSchoolId ?? undefined,
    })

    const conflictErrorDefinition = this.resourceConflict.errorHandlerDefinition()
    errorHandler.registerErrorDefinition(conflictErrorDefinition)

    this.errorHandler = errorHandler
  }

  createTeacher = task({ drop: true }, async () => {
    const { changeset, store } = this
    const { school, subscriptionType } = this.args

    const { email, firstName, lastName, accountType } = changeset
    // ensure these fields are string | undefined
    if (
      (typeof email !== 'string' && typeof email !== 'undefined') ||
      (typeof firstName !== 'string' && typeof firstName !== 'undefined') ||
      (typeof lastName !== 'string' && typeof lastName !== 'undefined')
    )
      return

    const teacher = store.createRecord<Teacher>('teacher', {
      email,
      login: email,
      firstName,
      lastName,
      school,
    })

    // artificially run 500ms of delay to make the button play nicer (it does anyway)
    await timeout(500)

    const includes = ['school-classes', 'teacher-subscriptions']
    await teacher.save({ adapterOptions: { include: includes.join(','), accountType, subscriptionType } })
    return teacher
  })

  createSchoolClass = task({ drop: true }, async (teacher: Teacher) => {
    const { store } = this
    const { subscriptionType } = this.args
    const subscriptionTypeName = this.intl.t(subscriptionType)
    const { school } = this.args
    const name = `${teacher.firstName} ${teacher.lastName}'s ${subscriptionTypeName} Class`
    const schoolClass = {
      name,
      subscriptionType,
      school,
      teachers: [teacher],
      gamesAccess: true,
      playroomAccess: true,
      backgroundMusicDisabled: false,
      mathseedsPrimeAccess: subscriptionType === SubscriptionType.Maths,
    }

    const schoolClassRecord = store.createRecord<SchoolClass>('school-class', schoolClass)
    await schoolClassRecord.save()
  })

  get generateTeacherSampleImportData() {
    const row = [
      this.intl.t('manage.bulkActions.csv.downloadSampleFields.firstName'),
      this.intl.t('manage.bulkActions.csv.downloadSampleFields.lastName'),
      this.intl.t('manage.bulkActions.csv.downloadSampleFields.email'),
      this.intl.t('manage.bulkActions.csv.downloadSampleFields.trialIndicator'),
    ]
    return Array.from({ length: 4 }, () => row)
  }

  @action
  async submit(event: Event) {
    event.preventDefault()

    const { accountType, firstName, lastName } = this.changeset
    assert('accountType must be string', typeof accountType === 'string')

    // Create teacher
    let teacher
    try {
      teacher = await this.createTeacher.perform()
    } catch (e: any) {
      if (didCancel(e)) return

      let name = ''
      if (typeof firstName === 'string' && typeof lastName === 'string') {
        name = this.intl.t('fullName', { firstName, lastName })
      }
      const message = this.intl.t(`manage.manageTeachers.create.${accountType}.error`, { name })

      // unload the record as we are not using it
      this.store
        .peekAll<Teacher>('teacher')
        .find((t) => t.isNew)
        ?.rollbackAttributes()

      const errors = e?.errors || []
      this.errorHandler.handle(errors)
      if (this.errorHandler.hasUnhandledErrors()) {
        this.flashQueue.addFail({ subtitle: message })
        this.errorHandler.sendOtherErrorsToLog(
          'Error while saving a new teacher from the "add-teacher-to-school" component',
        )
      }

      return
    }

    if (!teacher) return

    // Create a default school class for that teacher
    try {
      await this.createSchoolClass.perform(teacher)
    } catch (e: any) {
      if (didCancel(e)) return

      this.store
        .peekAll<SchoolClass>('school-class')
        .find((schoolClass) => schoolClass.isNew)
        ?.rollbackAttributes()

      this.flashQueue.addFail({ subtitle: this.intl.t('manage.manageTeachers.create.schoolClassCreationError') })
      this.log.error('Error while saving a new school-class from the "add-teacher-to-school" component', e)

      return
    }

    // success!
    let name = ''
    if (typeof firstName === 'string' && typeof lastName === 'string') {
      name = this.intl.t('fullName', { firstName, lastName })
    }
    const message = this.intl.t(`manage.manageTeachers.create.${accountType}.success`, { name })

    this.flashQueue.addSuccess({ subtitle: message })
    this.changeset.rollback()

    this.router.refresh('manage.teachers')
  }

  get submitDisabled() {
    return this.changeset.isInvalid || this.changeset.isPristine || this.isSubmitting
  }

  get isSubmitting() {
    return this.createTeacher.isRunning || this.createSchoolClass.isRunning
  }

  @action
  downloadSampleCsv() {
    const data = this.generateTeacherSampleImportData
    const filename = this.intl.t('manage.bulkActions.csv.downloadSampleFileName')
    this.fileDownload.downloadAsCsv(data, { filename })
  }

  @action
  selectCsvFile(fileArray: File[]) {
    if (fileArray && fileArray.length > 0) {
      const file = fileArray[0]
      this.args.onSelectCsvFile?.(file)
    }
  }

  <template>
    <form class="flex flex-row flex-wrap gap-2" {{on "submit" this.submit}} ...attributes>
      <FormField
        @changeset={{this.changeset}}
        @propertyName="firstName"
        @labelText={{t "manage.persons.edit.firstName"}}
        class="min-w-40 shrink-0 grow basis-0"
        as |field|
      >
        <field.input placeholder={{field.labelText}} aria-label={{field.labelText}} />
      </FormField>

      <FormField
        @changeset={{this.changeset}}
        @propertyName="lastName"
        @labelText={{t "manage.persons.edit.lastName"}}
        class="min-w-40 shrink-0 grow basis-0"
        as |field|
      >
        <field.input placeholder={{field.labelText}} aria-label={{field.labelText}} />
      </FormField>

      <FormField
        @changeset={{this.changeset}}
        @propertyName="email"
        @labelText={{t "manage.persons.edit.email"}}
        class="min-w-40 shrink-0 grow basis-0"
        as |field|
      >
        <field.input type="email" placeholder={{field.labelText}} aria-label={{field.labelText}} />
      </FormField>

      {{! Ensure these elements wrap together }}
      <div class="flex grow flex-row gap-2">
        <FormField
          @changeset={{this.changeset}}
          @propertyName="accountType"
          @labelText={{t "subscription"}}
          class="grow"
          as |field|
        >
          <field.select
            data-test-create-teacher-account-type
            aria-label={{field.labelText}}
            name="accountType"
            @options={{this.accountTypeOptions}}
            @disabled={{eq this.accountTypeOptions.length 1}}
            @valueLookupKey="value"
            @defaultText={{t "subscription"}}
          />
        </FormField>

        <UiButton
          data-test-create-teacher-submit
          type="submit"
          @disabled={{this.submitDisabled}}
          @appearance={{ButtonAppearance.regular}}
        >
          <UiButtonState @isLoading={{this.isSubmitting}}>
            {{t "manage.singularActions.submitGo"}}
          </UiButtonState>
        </UiButton>
      </div>
    </form>
    <HRText class="my-2" @text={{t "manage.bulkActions.csv.or"}} />
    <div class="flex items-center" data-test-upload-teachers-csv>
      <label for="upload-csv-input">
        <input
          id="upload-csv-input"
          class="hidden"
          type="file"
          accept="text/csv, .csv"
          disabled={{@isUploading}}
          {{on "change" (pick "target.files" this.selectCsvFile)}}
        />
        <UiButtonLink
          class="font-normal whitespace-nowrap"
          @appearance={{ButtonAppearance.regular}}
          @disabled={{@isUploading}}
        >
          {{#if @isUploading}}
            <FidgetSpinnerWaveComponent @small={{true}} @centered={{true}} />
          {{else}}
            {{t "manage.bulkActions.csv.upload"}}
          {{/if}}
        </UiButtonLink>
      </label>

      <span
        class="ml-4 cursor-pointer font-bold text-black hover:text-black focus:text-black"
        role="button"
        {{on "click" this.downloadSampleCsv}}
      >
        <span class="underline">
          {{t "manage.bulkActions.csv.downloadSample"}}
        </span>
      </span>
    </div>
  </template>
}

export default AddTeacherToSchool
